import { Link } from "react-router-dom";
import type { EventItem } from "@/mocks/events";
import StatusBadge from "@/components/feature/StatusBadge";
import { formatDate, getCategoryName, getCategoryIcon } from "@/lib/eventUtils";

const anchors = [
  { id: "overview", label: "事件概要" },
  { id: "timeline", label: "关键时间线" },
  { id: "qa", label: "关键问答" },
  { id: "related", label: "相关事件" },
];

export default function EventAside({ event }: { event: EventItem }) {
  const items = [
    { label: "所属方向", value: getCategoryName(event.category) },
    { label: "涉及范围", value: event.region },
    { label: "事件起点", value: formatDate(event.startDate) },
    { label: "最近更新", value: formatDate(event.updatedAt) },
    { label: "节点数量", value: `${event.timeline.length} 个` },
    { label: "关注热度", value: `${event.heat} / 100` },
  ];

  return (
    <div className="lg:sticky lg:top-24 flex flex-col gap-5">
      <div className="rounded-lg border border-background-200 bg-background-100 p-5">
        <div className="flex items-center justify-between">
          <h2 className="font-heading text-base font-bold text-foreground-950">事件速览</h2>
          <StatusBadge status={event.status} />
        </div>
        <dl className="mt-4 space-y-3">
          {items.map((item) => (
            <div key={item.label} className="flex items-start justify-between gap-4 text-sm">
              <dt className="text-foreground-500 shrink-0">{item.label}</dt>
              <dd className="text-foreground-900 text-right">{item.value}</dd>
            </div>
          ))}
        </dl>
        <div className="mt-4 pt-4 border-t border-background-200">
          <p className="text-xs text-foreground-500 mb-2">事件标签</p>
          <div className="flex flex-wrap gap-2">
            {event.tags.map((tag) => (
              <span
                key={tag}
                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-secondary-100 text-secondary-900 text-xs"
              >
                <i className="ri-hashtag" />
                {tag}
              </span>
            ))}
          </div>
        </div>
      </div>

      <nav
        aria-label="页内目录"
        className="rounded-lg border border-background-200 bg-background-50 p-5 hidden lg:block"
      >
        <h2 className="font-heading text-sm font-bold text-foreground-950">本页内容</h2>
        <ul className="mt-3 space-y-2 text-sm">
          {anchors.map((anchor) => (
            <li key={anchor.id}>
              <a
                href={`#${anchor.id}`}
                className="inline-flex items-center gap-2 text-foreground-600 hover:text-primary-700 cursor-pointer"
              >
                <i className="ri-subtract-line text-foreground-300" />
                {anchor.label}
              </a>
            </li>
          ))}
        </ul>
      </nav>

      <div className="rounded-lg border border-primary-200 bg-primary-50 p-5">
        <span className="w-9 h-9 flex items-center justify-center rounded-md bg-primary-100 text-primary-700">
          <i className="ri-upload-2-line" />
        </span>
        <h2 className="mt-3 font-heading text-sm font-bold text-foreground-950">
          你了解这起事件的后续吗
        </h2>
        <p className="mt-2 text-xs leading-relaxed text-foreground-600">
          如果你掌握尚未记录的节点，欢迎补充，我们会核实后更新到时间线。
        </p>
        <Link
          to="/#tips"
          className="mt-4 inline-flex w-full items-center justify-center gap-1.5 px-4 py-2.5 rounded-md text-sm font-medium whitespace-nowrap bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors cursor-pointer"
        >
          提供线索
          <i className="ri-arrow-right-line" />
        </Link>
      </div>

      <div className="rounded-lg border border-accent-200 bg-accent-50 p-5">
        <div className="flex items-center gap-2 text-accent-900">
          <i className={getCategoryIcon(event.category)} />
          <span className="font-heading text-sm font-bold">同方向事件</span>
        </div>
        <p className="mt-2 text-xs leading-relaxed text-foreground-700">
          想看更多「{getCategoryName(event.category)}」方向的事件，可以进入分类页集中浏览。
        </p>
        <Link
          to={`/events?cat=${event.category}`}
          className="mt-3 inline-flex items-center gap-1 text-xs font-medium text-accent-800 hover:text-accent-700 cursor-pointer"
        >
          浏览该方向
          <i className="ri-arrow-right-line" />
        </Link>
      </div>
    </div>
  );
}